import { useState } from 'react';

export default function Services() {
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  const [expandedChannels, setExpandedChannels] = useState(false);
  const [showStandardServices, setShowStandardServices] = useState(false);
  const [showPremiumPartnership, setShowPremiumPartnership] = useState(false);
  const [showOzonConsulting, setShowOzonConsulting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const premiumServices = [
    {
      id: 'service-digital-360',
      title: 'Digital Marketing 360°',
      subtitle: 'Building a Digital Business Ecosystem',
      description: 'A comprehensive solution that unites all online channels into a single mechanism. We don\'t just launch advertising, we create a digital environment around your brand, using AI technologies to maximize reach and conversion.',
      icon: 'ri-global-line',
      color: 'from-slate-900 via-blue-900 to-slate-900',
      features: [
        'Development of omnichannel strategy (website + social media + newsletters + advertising)',
        'Implementation of end-to-end analytics',
        'Communication automation (chatbots, auto-funnels)',
        'Online reputation management (SERM)',
        'Dominance in digital space'
      ],
      result: 'Your company dominates the digital space, and the client "touches" your brand at all stages of decision-making.',
      cta: 'Discuss Ecosystem'
    },
    {
      id: 'service-complex-strategy',
      title: 'Comprehensive Strategy',
      subtitle: 'Turnkey Development',
      description: 'Complete digital transformation of your business marketing. We create a working system from analysis to implementation that brings stable results.',
      icon: 'ri-compass-3-line',
      color: 'from-indigo-900 via-purple-900 to-indigo-900',
      features: [
        'Deep analysis of business and competitors',
        'Building sales funnel',
        'Selection of optimal channels',
        'Implementation plan for 6-12 months',
        'Training your team'
      ],
      cta: 'Get Development Plan'
    },
    {
      id: 'service-consulting',
      title: 'Marketing Consulting',
      subtitle: 'Strategic Top-Down View',
      description: 'Deep audit of your business and search for growth points. We find hidden opportunities and provide a clear action plan for scaling.',
      icon: 'ri-lightbulb-line',
      color: 'from-red-900 via-rose-900 to-red-900',
      features: [
        'Current marketing audit',
        'Competitor analysis using AI',
        'Search for growth points',
        'Personal presentation of results',
        'Development roadmap'
      ],
      cta: 'Discover Growth Points'
    },
    {
      id: 'service-partnership',
      title: 'Strategic Management and Partnership',
      subtitle: 'Premium service for manufacturers on WB and Ozon',
      description: 'We don\'t work as a conveyor agency. We enter your business as Partners who take responsibility for creating a marketing system and final profit.',
      icon: 'ri-vip-diamond-line',
      color: 'from-purple-900 via-pink-900 to-purple-900',
      isPremiumPartnership: true,
      forWhom: 'For Manufacturers who are ready to delegate the building of a sales department on marketplaces to professional Managers.',
      comparisonTable: {
        title: 'Managing Partner vs Regular Manager',
        subtitle: 'Why you need a strategist, not just an executor?',
        rows: [
          {
            aspect: 'Position',
            manager: 'Hired employee performing tasks. Waits to be told what to do.',
            partner: 'Business partner. We take initiative, tell the owner what to do for growth. Treat the project as our own.'
          },
          {
            aspect: 'Motivation',
            manager: 'Salary. Often not motivated to do more than minimum.',
            partner: 'Results and Profit. Work for Fix (covering costs) + % of net profit. Interested in optimizing your expenses.'
          },
          {
            aspect: 'Area of Responsibility',
            manager: 'Partial (cards, review responses). Requires constant control and checks.',
            partner: 'All business processes "turnkey". Self-managing. Build and control marketing department.'
          },
          {
            aspect: 'Development',
            manager: 'Works with what exists.',
            partner: 'Work with assortment matrix, propose new products, launch new directions and sales channels.'
          }
        ]
      },
      approach: {
        title: 'Our Approach: Depth Instead of Mass',
        subtitle: 'We work individually and don\'t take projects in "batches".',
        points: [
          {
            label: 'Focus',
            text: 'Take maximum 1-2 projects simultaneously.'
          },
          {
            label: 'Immersion',
            text: 'We fully integrate into your business. We\'re not outside observers, we feel the project\'s pulse, control every number and see the situation from inside.'
          },
          {
            label: 'Team',
            text: 'One person can\'t do it all. Marketplaces are a complex system. Our task is not to do everything alone, but to build an effective team: improving current employees\' efficiency, automating routine, hiring and training new specialists for strategy tasks.'
          }
        ],
        philosophy: 'We work for your personal income. The high price is due to the fact that we spend our time, expertise on you and guarantee deep immersion unavailable to regular agencies.'
      },
      credentials: {
        title: 'Why are we trusted with business management?',
        subtitle: 'Our leader\'s path is rapid growth from specialist to manager, confirmed by results in top market teams.',
        achievements: [
          {
            title: 'School of Market Leaders',
            text: 'Experience working in the team of top seller and coach Leo Shevchenko (Consulting agency "Mercatus"). We know how the best strategies work from inside.'
          },
          {
            title: 'Record Career Growth',
            points: [
              'In 3 months: Growth from line Manager position to Head of manager group at "Mercatus" agency thanks to systematic approach and leadership qualities.',
              'In 1 year: Path from Manager to Managing Strategist who builds marketing departments from scratch. Training and practice at Convert Monster advertising agency'
            ]
          },
          {
            title: 'Qualification',
            text: 'Professional education: "Marketplace Sales Manager" (December 2021). Expertise in launching different niches and understanding marketplaces as a complex ecosystem where every detail matters: from SEO to Unit economics.'
          }
        ]
      },
      cta: 'Discuss Partnership'
    },
    {
      id: 'service-ozon',
      title: 'Ozon Marketplace Consulting',
      subtitle: 'Numbers, Analytics, Profit, AI-Automation',
      description: 'Marketplace is primarily mathematics. We don\'t just "promote cards", we implement a management accounting system so you trade in profit, not for turnover.',
      icon: 'ri-line-chart-line',
      color: 'from-emerald-900 via-teal-900 to-emerald-900',
      features: [
        'Unit economics: calculation for each SKU (cost, logistics, commissions)',
        'Metrics control: margin, gross profit, profitability, ROI',
        'Reporting system: weekly and monthly sales reports',
        'Smart logistics: supply planning and inventory control',
        'Technological automation: VBA, macros, AI for Big Data processing',
        'External traffic: social media, context, targeting, bloggers, landing pages',
        'Management accounting (DDS) to avoid cash gaps'
      ],
      result: 'You see not just "some sales", but a clear financial model of your marketplace business.',
      result2: 'You get a technological partner who manages your store with machine precision and AI speed.',
      cta: 'Calculate Unit Economics',
      sections: [
        {
          emoji: '💰',
          title: 'Unit Economics and Finance',
          subtitle: 'Foundation',
          description: 'We count money before spending it. No guesswork.',
          points: [
            'Detailed calculation for each SKU: Cost, logistics, commissions',
            'Real-time key metrics control: Margin, Gross profit, % Markup, Profitability, ROI',
            'Promotion calculation: mathematical calculation of product participation in promotions',
            'Management accounting (DDS): maintaining Cash Flow Statement'
          ]
        },
        {
          emoji: '📈',
          title: 'Transparent Reporting System',
          subtitle: 'Control',
          description: 'You always keep your finger on the business pulse thanks to regular reports.',
          points: [
            'Weekly reports: dynamics of sales, orders and redemptions',
            'Monthly summary report: detailed analytics by articles',
            'Internal advertising reporting: pay per click and pay per order'
          ]
        },
        {
          emoji: '📦',
          title: 'Smart Logistics and Planning',
          subtitle: 'Supply Chain',
          description: 'Eliminate Out-of-Stock and Overstock situations.',
          points: [
            'Summary table of balances: deep analysis of stocks by each cluster and warehouse',
            'Supply planning: automatic calculation of necessary assortment',
            'Supply formation: preparation of summary supply file for uploading to Ozon personal account in one click'
          ]
        },
        {
          emoji: '🤖',
          title: 'Technological Automation',
          subtitle: 'VBA + AI',
          description: 'Our competitive advantage is seeing what can be automated and implementing it.',
          points: [
            'Working with Big Data: easily process data arrays for 1000+ articles and millions of Excel rows',
            'VBA and Macros: use Visual Basic programming language to write scripts',
            'Artificial Intelligence: apply latest neural network versions for data analysis and optimization search',
            'Result: You pay for strategy and results, not for hours of manual work in tables'
          ]
        },
        {
          emoji: '🌐',
          title: 'External Traffic',
          subtitle: 'Scaling',
          description: 'Ozon encourages external traffic, and we know how to work with it.',
          points: [
            'Social media: management and audience warming',
            'Paid advertising: context (Yandex) and targeting for products',
            'Working with bloggers: integrations with opinion leaders',
            'Landing pages: creating separate sites for collecting external audience'
          ]
        }
      ]
    }
  ];

  const services = [
    {
      title: 'Contextual Advertising',
      subtitle: 'Yandex.Direct, Google Ads',
      emoji: '🎯',
      tagline: 'Catching "hot" demand',
      description: 'We work with clients who are already looking for your product. We don\'t just set up campaigns, we implement a bid management system for maximum ROI.',
      icon: 'ri-search-line',
      color: 'from-blue-500 to-cyan-500',
      features: [
        'Semantic core collection, minus words',
        'Writing selling ads',
        'Retargeting',
        'Constant analytics and cost per click reduction'
      ],
      result: 'Targeted traffic from day one of launch'
    },
    {
      title: 'Targeted Advertising',
      subtitle: 'Instagram, Facebook, VKontakte',
      emoji: '⚡️',
      tagline: 'Precise targeting of your audience',
      description: 'We use social network algorithms and AI to find your clients even before they start looking for you. We create creatives that break through "banner blindness".',
      icon: 'ri-focus-3-line',
      color: 'from-purple-500 to-pink-500',
      features: [
        'Audience segmentation',
        'A/B testing of creatives',
        'Pixel setup for conversion tracking',
        'Scaling successful combinations'
      ],
      result: 'Flow of requests from audience most interested in your offer'
    },
    {
      title: 'SMM: Content and Management',
      subtitle: 'Instagram, Facebook, VK, Telegram',
      emoji: '💎',
      tagline: 'Content that sells without words',
      description: 'We turn your social media from a boring "bulletin board" into a living community. We develop a unified visual style and Tone of Voice that inspires trust and loyalty.',
      icon: 'ri-chat-smile-3-line',
      color: 'from-green-500 to-emerald-500',
      features: [
        'Content strategy',
        'Professional copywriting',
        'Post and story design',
        'Administration and comment management',
        'Telegram channel development'
      ],
      result: 'Growth of subscriber base, engagement and repeat sales through social media'
    },
    {
      title: 'Creating Selling Websites',
      subtitle: 'Your main sales machine 24/7',
      emoji: '💻',
      tagline: 'Marketing tool with high conversion',
      description: 'We don\'t make websites "for beauty". We create marketing tools with high conversion. Thoughtful structure leads the visitor by the hand from headline to "Buy" button.',
      icon: 'ri-window-line',
      color: 'from-orange-500 to-amber-500',
      features: [
        'Prototyping',
        'UX/UI design',
        'Layout, mobile adaptation',
        'Basic SEO optimization',
        'CRM integration'
      ],
      result: 'Website that turns visitors into requests, not just hangs on the internet'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('contact', formData.contact);
      if (formData.website) {
        formBody.append('website', formData.website);
      }

      const response = await fetch('https://readdy.ai/api/form/d4jj1ukucjmktht589ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', contact: '', website: '' });
        setTimeout(() => {
          setShowPopup(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="services" className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose a ready-made solution or we will create an individual strategy for your business
            </p>
          </div>

          {/* Premium Services - Full Width Cards */}
          <div className="space-y-8 mb-16">
            {premiumServices.map((service, index) => (
              service.isPremiumPartnership ? (
                // Premium Partnership - Collapsible Button
                <div key={index} id={service.id}>
                  <button
                    onClick={() => setShowPremiumPartnership(!showPremiumPartnership)}
                    className="w-full bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-10 shadow-2xl relative overflow-hidden group hover:shadow-purple-500/30 transition-all duration-500 text-left cursor-pointer"
                  >
                    <div className="relative z-10">
                      <div className="flex items-start justify-between gap-6">
                        <div className="flex-1 text-center">
                          <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-sm mb-4">
                            💎 PREMIUM SERVICE
                          </div>
                          <div className="mb-4">
                            <p className="text-purple-200 text-lg font-semibold mb-2">
                              Premium service for manufacturers on WB and Ozon
                            </p>
                            <h3 className="text-3xl font-bold text-white">
                              Strategic Management and Partnership
                            </h3>
                          </div>
                        </div>
                        <div className={`w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${showPremiumPartnership ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-3xl text-white"></i>
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {showPremiumPartnership && (
                    <div 
                      className="mt-8 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-12 shadow-2xl relative overflow-hidden animate-fadeIn"
                    >
                      <div 
                        className="absolute inset-0 opacity-10"
                        style={{
                          backgroundImage: `url('https://public.readdy.ai/ai/img_res/2a54ab76a6434db9c4a8a8a79728cd5f.jpg')`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      
                      <div className="relative z-10">
                        <div className="text-center mb-12">
                          <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto mb-4">
                            {service.description}
                          </p>
                          <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-5 max-w-3xl mx-auto">
                            <p className="text-sm font-semibold text-pink-300 mb-2">For whom:</p>
                            <p className="text-white text-base leading-relaxed">{service.forWhom}</p>
                          </div>
                        </div>

                        {/* Comparison Table */}
                        <div className="mb-12">
                          <div className="text-center mb-8">
                            <h4 className="text-3xl font-bold text-white mb-3">🤝 {service.comparisonTable.title}</h4>
                            <p className="text-lg text-gray-300">{service.comparisonTable.subtitle}</p>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                              <thead>
                                <tr className="bg-white/10">
                                  <th className="px-6 py-4 text-left text-white font-bold"></th>
                                  <th className="px-6 py-4 text-center text-white font-bold">
                                    <div className="flex items-center justify-center gap-2">
                                      <span className="text-2xl">👷</span>
                                      <span>Regular Manager<br/><span className="text-sm font-normal text-gray-400">("Hands")</span></span>
                                    </div>
                                  </th>
                                  <th className="px-6 py-4 text-center text-white font-bold bg-gradient-to-r from-pink-500/20 to-purple-500/20">
                                    <div className="flex items-center justify-center gap-2">
                                      <span className="text-2xl">👑</span>
                                      <span>Managing Partner<br/><span className="text-sm font-normal text-pink-300">("Head and System")</span></span>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {service.comparisonTable.rows.map((row, idx) => (
                                  <tr key={idx} className="border-t border-white/10">
                                    <td className="px-6 py-4 font-bold text-pink-300">{row.aspect}</td>
                                    <td className="px-6 py-4 text-gray-400 text-sm">{row.manager}</td>
                                    <td className="px-6 py-4 text-white text-sm bg-gradient-to-r from-pink-500/10 to-purple-500/10">{row.partner}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Approach Section */}
                        <div className="mb-12 bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                          <div className="text-center mb-8">
                            <h4 className="text-3xl font-bold text-white mb-3">🚀 {service.approach.title}</h4>
                            <p className="text-lg text-gray-300">{service.approach.subtitle}</p>
                          </div>
                          <div className="grid md:grid-cols-3 gap-6 mb-8">
                            {service.approach.points.map((point, idx) => (
                              <div key={idx} className="bg-white/5 rounded-xl p-6 border border-white/10">
                                <p className="text-pink-300 font-bold text-lg mb-3">{point.label}:</p>
                                <p className="text-gray-300 text-sm leading-relaxed">{point.text}</p>
                              </div>
                            ))}
                          </div>
                          <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl p-6">
                            <p className="text-sm font-semibold text-pink-300 mb-2">Our philosophy:</p>
                            <p className="text-white text-base leading-relaxed">{service.approach.philosophy}</p>
                          </div>
                        </div>

                        {/* Credentials Section */}
                        <div className="mb-12">
                          <div className="text-center mb-8">
                            <h4 className="text-3xl font-bold text-white mb-3">🏆 {service.credentials.title}</h4>
                            <p className="text-lg text-gray-300">{service.credentials.subtitle}</p>
                          </div>
                          <div className="grid md:grid-cols-3 gap-6">
                            {service.credentials.achievements.map((achievement, idx) => (
                              <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                                <h5 className="text-xl font-bold text-pink-300 mb-4">{achievement.title}</h5>
                                {achievement.text && (
                                  <p className="text-gray-300 text-sm leading-relaxed">{achievement.text}</p>
                                )}
                                {achievement.points && (
                                  <ul className="space-y-3">
                                    {achievement.points.map((point, pidx) => (
                                      <li key={pidx} className="flex items-start gap-2">
                                        <div className="w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                          <i className="ri-check-line text-white text-xs"></i>
                                        </div>
                                        <span className="text-gray-200 text-sm">{point}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="text-center">
                          <button 
                            onClick={() => {
                              const element = document.getElementById('lead-form');
                              element?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="px-10 py-6 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl transition-all duration-300 shadow-lg shadow-pink-500/50 hover:shadow-pink-500/70 whitespace-nowrap cursor-pointer"
                          >
                            <div className="text-xl font-bold mb-1">Book Strategic Interview</div>
                            <div className="text-sm font-normal opacity-90">Let's discuss your project and see if we're a good fit for partnership.</div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : service.sections ? (
                // Ozon Service - Collapsible Button
                <div key={index} id={service.id}>
                  <button
                    onClick={() => setShowOzonConsulting(!showOzonConsulting)}
                    className="w-full bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 rounded-3xl p-10 shadow-2xl relative overflow-hidden group hover:shadow-cyan-500/30 transition-all duration-500 text-left cursor-pointer"
                  >
                    <div className="relative z-10">
                      <div className="flex items-start justify-between gap-6">
                        <div className="flex-1 text-center">
                          <div className="flex items-center justify-center gap-4 mb-4">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <i className={`${service.icon} text-4xl text-white`}></i>
                            </div>
                            <div>
                              <h3 className="text-3xl font-bold text-white mb-2">
                                {service.title}
                              </h3>
                              <p className="text-cyan-100 text-lg font-semibold">
                                {service.subtitle}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className={`w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${showOzonConsulting ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-3xl text-white"></i>
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {showOzonConsulting && (
                    <div 
                      className="mt-8 bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 rounded-3xl p-12 shadow-2xl relative overflow-hidden animate-fadeIn"
                    >
                      <div 
                        className="absolute inset-0 opacity-10"
                        style={{
                          backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20digital%20marketing%20ecosystem%20with%20interconnected%20channels%20and%20data%20flows%2C%20modern%20technology%20network%20visualization%20with%20glowing%20nodes%20and%20connections%2C%20dark%20blue%20and%20cyan%20color%20scheme%2C%20futuristic%20business%20intelligence%20dashboard%20concept%20with%20geometric%20patterns%20and%20neural%20network%20elements%2C%20professional%20consulting%20strategy%20background%20with%20data%20analytics%20visualization&width=1400&height=600&seq=service-premium-00${index}&orientation=landscape')`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      
                      <div className="relative z-10">
                        <div className="text-center mb-12">
                          <p className="text-gray-300 text-xl leading-relaxed max-w-4xl mx-auto mb-8">
                            {service.description}
                          </p>
                        </div>

                        {/* Five Sections */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                          {service.sections.map((section, idx) => (
                            <div key={idx} className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 ${idx === 3 || idx === 4 ? 'md:col-span-1' : ''}`}>
                              <div className="text-4xl mb-4">{section.emoji}</div>
                              <h4 className="text-xl font-bold text-white mb-2">{section.title}</h4>
                              <p className="text-sm text-cyan-300 font-semibold mb-3">{section.subtitle}</p>
                              <p className="text-gray-300 text-sm mb-4 leading-relaxed">{section.description}</p>
                              <ul className="space-y-2">
                                {section.points.map((point, pidx) => (
                                  <li key={pidx} className="flex items-start gap-2">
                                    <div className="w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                      <i className="ri-check-line text-white text-xs"></i>
                                    </div>
                                    <span className="text-gray-200 text-sm">{point}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Result and CTA */}
                        <div className="text-center">
                          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6 mb-8 max-w-3xl mx-auto space-y-3">
                            <div>
                              <p className="text-sm font-semibold text-cyan-300 mb-2">✅ Result:</p>
                              <p className="text-white text-lg leading-relaxed">{service.result}</p>
                            </div>
                            {service.result2 && (
                              <p className="text-white text-lg leading-relaxed">{service.result2}</p>
                            )}
                          </div>
                          <button 
                            onClick={() => {
                              const element = document.getElementById('lead-form');
                              element?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="px-10 py-5 bg-cyan-500 hover:bg-cyan-600 text-white text-lg font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/50 hover:shadow-cyan-500/70 whitespace-nowrap cursor-pointer"
                          >
                            {service.cta}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Other Premium Services
                <div 
                  key={index}
                  id={service.id}
                  className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-3xl p-12 shadow-2xl relative overflow-hidden group hover:shadow-cyan-500/20 transition-all duration-500"
                  style={{
                    background: `linear-gradient(135deg, ${service.color.split(' ')[0].replace('from-', '')} 0%, ${service.color.split(' ')[1].replace('via-', '')} 50%, ${service.color.split(' ')[2].replace('to-', '')} 100%)`
                  }}
                >
                  <div 
                    className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20digital%20marketing%20ecosystem%20with%20interconnected%20channels%20and%20data%20flows%2C%20modern%20technology%20network%20visualization%20with%20glowing%20nodes%20and%20connections%2C%20dark%20blue%20and%20cyan%20color%20scheme%2C%20futuristic%20business%20intelligence%20dashboard%20concept%20with%20geometric%20patterns%20and%20neural%20network%20elements%2C%20professional%20consulting%20strategy%20background%20with%20data%20analytics%20visualization&width=1400&height=600&seq=service-premium-00${index}&orientation=landscape')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  
                  <div className="relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                      <div>
                        <div className="w-20 h-20 bg-cyan-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                          <i className={`${service.icon} text-5xl text-cyan-400`}></i>
                        </div>
                        <h3 className="text-4xl font-bold text-white mb-3">
                          {service.title}
                        </h3>
                        <p className="text-xl text-cyan-300 mb-6 font-semibold">
                          {service.subtitle}
                        </p>
                        <p className="text-gray-300 text-lg leading-relaxed mb-8">
                          {service.description}
                        </p>
                        {service.result && (
                          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-5 mb-8">
                            <p className="text-sm font-semibold text-cyan-300 mb-2">Result:</p>
                            <p className="text-white text-base leading-relaxed">{service.result}</p>
                          </div>
                        )}
                        <button 
                          onClick={() => {
                            const element = document.getElementById('lead-form');
                            element?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/50 hover:shadow-cyan-500/70 whitespace-nowrap cursor-pointer"
                        >
                          {service.cta}
                        </button>
                      </div>
                      
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                        <h4 className="text-xl font-bold text-white mb-6">What's included:</h4>
                        <ul className="space-y-4">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                <i className="ri-check-line text-white text-sm"></i>
                              </div>
                              <span className="text-gray-200 text-lg">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Standard Services - Collapsible Button */}
          <div className="mb-12" id="service-standard">
            <button
              onClick={() => setShowStandardServices(!showStandardServices)}
              className="w-full bg-gradient-to-br from-rose-600 via-orange-600 to-amber-600 rounded-3xl p-10 shadow-2xl relative overflow-hidden group hover:shadow-orange-500/30 transition-all duration-500 text-left cursor-pointer"
            >
              <div className="relative z-10">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1 text-center">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-rocket-line text-4xl text-white"></i>
                      </div>
                      <div>
                        <p className="text-white/90 text-sm font-semibold mb-1">📢 Individual Promotion Channels</p>
                        <h3 className="text-3xl font-bold text-white">
                          Complex of Digital Tools for Growth
                        </h3>
                      </div>
                    </div>
                    <p className="text-white/90 text-lg mb-4">
                      Contextual Advertising • Targeted Advertising • SMM • Website Creation
                    </p>
                    <p className="text-white/80 text-base">
                      Choose individual tools for targeted marketing enhancement or use them in complex for maximum effect
                    </p>
                  </div>
                  <div className={`w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${showStandardServices ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-3xl text-white"></i>
                  </div>
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {showStandardServices && (
              <div className="mt-8 grid md:grid-cols-2 gap-8 animate-fadeIn">
                {services.map((service, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-100"
                  >
                    <div className={`bg-gradient-to-br ${service.color} p-8 text-white relative overflow-hidden`}>
                      <div className="absolute top-4 right-4 text-5xl opacity-20">
                        {service.emoji}
                      </div>
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <i className={`${service.icon} text-3xl`}></i>
                      </div>
                      <h3 className="text-2xl font-bold mb-2">
                        {service.title}
                      </h3>
                      <p className="text-sm text-white/90 font-medium mb-3">
                        {service.subtitle}
                      </p>
                      <p className="text-lg font-bold text-white/95">
                        {service.tagline}
                      </p>
                    </div>
                    
                    <div className="p-6">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        {service.description}
                      </p>
                      
                      <div className="mb-6">
                        <h4 className="text-sm font-bold text-slate-900 mb-3">What's included:</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                              <i className="ri-check-line text-cyan-500 mt-0.5 flex-shrink-0"></i>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-cyan-700 mb-1">Result:</p>
                        <p className="text-sm text-gray-800 font-medium">{service.result}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-16 text-center">
            <button 
              onClick={() => {
                const element = document.getElementById('lead-form');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-lg font-semibold rounded-xl transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
            >
              Calculate Service Cost
            </button>
          </div>
        </div>
      </section>

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-8 md:p-10">
              {/* Close Button */}
              <button
                onClick={() => setShowPopup(false)}
                className="float-right text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-3xl"></i>
              </button>

              {/* Header */}
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Is your marketing working at 100% or draining budget?
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Don't guess. Get an honest outside view. Book a free 20-minute express diagnostics of your project.
                </p>
              </div>

              {/* Benefits */}
              <div className="mb-8 space-y-4">
                <h3 className="text-xl font-semibold text-cyan-400 mb-4">What you'll learn:</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🔍</span>
                    <p className="text-gray-300 leading-relaxed">
                      Where you're losing clients right now (finding "holes" in the funnel).
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📈</span>
                    <p className="text-gray-300 leading-relaxed">
                      3 non-obvious growth points for scaling revenue.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🤖</span>
                    <p className="text-gray-300 leading-relaxed">
                      How AI can reduce your advertising costs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} data-readdy-form id="diagnostic-form" className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone / Telegram <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact"
                    name="contact"
                    required
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors text-sm"
                    placeholder="+7 (999) 123-45-67 or @username"
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-300 mb-2">
                    Website link <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors text-sm"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Submit Status */}
                {submitStatus === 'success' && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <p className="text-green-400 text-center font-medium">
                      ✓ Request successfully sent! We will contact you shortly.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <p className="text-red-400 text-center font-medium">
                      ✗ Submission error. Please try again.
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/50 hover:shadow-cyan-500/70 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Discover Growth Points'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
